import { useState } from 'react'

import type { Expense } from '@subaio/api-types/budget'
import { ButtonGroup, Collapse, FormattedMessage } from '@subaio/components'

import { Cell } from 'components/Lists'
import { useModal } from 'components/Modal'

import { ChevronFlip } from '../ChevronFlip'
import { ButtonLink } from './ButtonLink'
import { Container } from './Container'
import { Content } from './Content'
import { Header } from './Header'
import { UncategorizeForm } from './UncategorizedForm'
import { UncategorizedService } from './UncategorizedService'

const defaultVisibleServices = 3

type Props = {
	uncategorized: Array<Expense>
	isUncategorizedEnabled: boolean
	currency: string
	categoriesMap: Array<{
		label: string
		value: {
			id: string
			subCategoryId: string
		}
	}>

	onRecategorize: (args: { categoryId: string; subCategoryId: string; serviceId: string }) => Promise<void>
}

export const Uncategorized = ({
	isUncategorizedEnabled,
	currency,
	uncategorized,
	categoriesMap,
	onRecategorize,
}: Props) => {
	const { showCard } = useModal()
	const [isOpen, setIsOpen] = useState(true)
	const [isFullListShown, setIsFullListShown] = useState(false)

	if (uncategorized.length === 0 || !isUncategorizedEnabled) return null

	return (
		<Container>
			<Header isOpen={isOpen} onClick={() => setIsOpen((isOpen) => !isOpen)}>
				<Cell shouldExpand>
					<FormattedMessage
						id='flow.budgetEditForm.uncategorized.title'
						defaultMessage='Should these expenses be included in your budget?'
					/>
				</Cell>
				<Cell textAlign='center'>
					<ChevronFlip isActive={isOpen} />
				</Cell>
			</Header>
			<Collapse isOpen={isOpen}>
				<Content>
					<p>
						<FormattedMessage
							id='flow.budgetEditForm.uncategorized.description1'
							defaultMessage="Start by looking at the expenses below. We've found some expenses that we're not sure whether should be included in your budget."
						/>
					</p>
					<FormattedMessage
						id='flow.budgetEditForm.uncategorized.description2'
						defaultMessage="Review the expenses and click ‘categorize’ to add them as fixed expenses in your budget. If some of the expenses are not relevant to the budget, you don't need to do anything about them."
					/>
				</Content>
				{uncategorized.slice(0, isFullListShown ? uncategorized.length : defaultVisibleServices).map((expense) => (
					<UncategorizedService
						key={expense.id}
						amountToShow='default'
						{...expense}
						currency={currency}
						onClick={(service) =>
							showCard(({ onClose }) => (
								<UncategorizeForm
									service={service}
									currency={currency}
									categoriesMap={categoriesMap}
									onClose={onClose}
									onRecategorize={async (props) => {
										await onRecategorize(props)
										onClose()
									}}
								/>
							))
						}
					/>
				))}
				{uncategorized.length > defaultVisibleServices && (
					<ButtonGroup center>
						<ButtonLink
							label={
								isFullListShown ? (
									<FormattedMessage id='flow.budgetEditForm.uncategorized.labelShowLess' defaultMessage='Show less' />
								) : (
									<FormattedMessage id='flow.budgetEditForm.uncategorized.labelShowMore' defaultMessage='Show more' />
								)
							}
							onClick={() => setIsFullListShown((isFullListShown) => !isFullListShown)}
						/>
					</ButtonGroup>
				)}
			</Collapse>
		</Container>
	)
}
